import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box, Link, Typography } from '@material-ui/core';
import featureServicePlanItemStyles from './featureServicePlanItemStyles';
import clsx from 'clsx';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Checkbox from '@material-ui/core/Checkbox';
import IFeature from '../../../../../../model/IFeature';
import FeatureServicePlanItemDetails from '../featureServicePlanItemDetails/FeatureServicePlanItemDetails';
import Collapse from '@material-ui/core/Collapse';
import Translation from '../../../../../../../gqlCommon/components/translation/Translation';
import useFeatureChildren from '../../../../../../hooks/queries/useFeatureChildren';
import { GetFeatureChildren_featureChildren } from '../../../../../../graphql/queries/__generated__/GetFeatureChildren';
import Price from '../../../../../../../gqlCommon/components/presentation/Price';
import { PriceDisplayType } from '../../../../../../../../../common/enum/PriceDisplayType';
import usePrice from '../../../../../../../gqlCommon/hooks/prices/usePrice';
import CategoryFeatureTypes from '../../../../../../enum/CategoryFeatureTypes';
import useCustomizeCarContext from '../../../../../../hooks/useCustomizeCarContext';

export interface IFeatureServicePlanItemProps {
  feature: IFeature;
  selected: boolean;
  onClick: (id: string) => void;
}

const FeatureServicePlanItem: FunctionComponent<IFeatureServicePlanItemProps> = ({ selected, feature, onClick }) => {
  const { name, prices, code } = feature;
  const styles = featureServicePlanItemStyles();
  const [open, setOpen] = useState(false);
  const { selectedCar } = useCustomizeCarContext();
  const [featureChildren, setFeatureChildren] = useState<GetFeatureChildren_featureChildren[]>([]);
  const [isChecked, setIsChecked] = useState<boolean>(selected);
  const { data, loading, loadFeatureChildren } = useFeatureChildren();
  const { primaryPrice } = usePrice(prices);
  const carCode = selectedCar?.car?.byoCode;

  useEffect(() => {
    if (carCode) {
      loadFeatureChildren({
        carCode,
        featureCode: code,
      });
    }
  }, [loadFeatureChildren, code, carCode]);

  useEffect(() => {
    if (!loading && data) {
      setFeatureChildren(data.featureChildren);
    }
  }, [loading, data]);

  useEffect(() => {
    setIsChecked(selected);
  }, [selected]);

  const price = (
    <Price
      className={clsx(styles.priceValue)}
      price={primaryPrice?.money.amount}
      data-test={'customise:featureServicePlanPrice'}
      priceDisplayType={PriceDisplayType.PRIMARY_OPTIONS}
    />
  );

  return (
    <Box
      className={clsx([styles.container, { [styles.planSelected]: isChecked }])}
      data-test={`customise:featureServicePlan:${code}`}
    >
      <Box display="flex" alignItems="flex-start">
        <Checkbox
          data-test="customise:featureServicePlanToggle"
          className={styles.servicePlanCheckbox}
          checked={isChecked}
          onChange={() => {
            onClick(code);
            setIsChecked(!isChecked);
          }}
          data-selected={isChecked}
        />
        <Box className={styles.titlePriceContainer}>
          <Box>
            <Typography
              data-test="customise:featureServicePlanName"
              {...(selected && { 'data-option': `${CategoryFeatureTypes.SERVICE_PLAN}:selected` })}
            >
              {name}
            </Typography>
            <Link
              className={styles.priceGuideToggle}
              onClick={() => setOpen(!open)}
              data-test="customise:featureServicePlanPriceGuide"
            >
              <Translation id="trans__configurator__customise_page_service_section__price_guide_link" />
              <ExpandMoreIcon className={clsx([styles.expandIcon, { [styles.invertedIcon]: open }])} />
            </Link>
          </Box>
          {price}
        </Box>
      </Box>
      {
        <Collapse in={open}>
          <FeatureServicePlanItemDetails planPrice={primaryPrice} serviceIntervals={featureChildren} />
        </Collapse>
      }
    </Box>
  );
};

export default FeatureServicePlanItem;
